import { Currency, V3_FACTORY_ADDRESSES } from '@kodiak-finance/sdk-core'
import { computePoolAddress, FeeAmount } from '@kodiak-finance/v3-sdk'
import { ButtonGray } from 'components/Button'
import Card from 'components/Card'
import { AutoColumn } from 'components/Column'
import { RowBetween } from 'components/Row'
import * as T from 'components/Typography'
import { useConnection } from 'hooks/useConnection'
import { useFeeTierDistribution } from 'hooks/useFeeTierDistribution'
import { PoolState } from 'hooks/usePools'
import usePrevious from 'hooks/usePrevious'
import { useV3Pools } from 'hooks/useV3Pools'
import { DynamicSection } from 'pages/AddLiquidity/styled'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { ThemedText } from 'theme'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import { FeeOption } from './FeeOption'
import { FEE_AMOUNT_DETAIL } from './shared'

const pulse = (color: string) => keyframes`
  0% { box-shadow: 0 0 0 0 ${color}; }
  70% { box-shadow: 0 0 0 2px ${color}; }
  100% { box-shadow: 0 0 0 0 ${color}; }
`

const CARD_MIN_HEIGHT_PX = 96

const FocusedOutlineCard = styled(Card)<{ pulsing: boolean }>`
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background-color: ${({ theme }) => theme.cardSecondary};
  border-radius: 12px;
  animation: ${({ pulsing, theme }) => pulsing && pulse(theme.deprecated_primary1)} 0.6s linear;

  min-height: ${CARD_MIN_HEIGHT_PX}px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
`

const FeeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  ${({ theme }) => theme.mediaWidth.upToSmall` gap: 6px; `}
`

const TopBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  line-height: 1;
  background: ${({ theme }) => theme.cardPrimary};
  color: ${({ theme }) => theme.textSecondary};
  min-height: 20px;
`

const OptionsRegion = styled.div<{ hidden_: boolean }>`
  transition: opacity 140ms ease-in-out, filter 140ms ease-in-out;
  opacity: ${({ hidden_ }) => (hidden_ ? 0 : 1)};
  visibility: ${({ hidden_ }) => (hidden_ ? 'hidden' : 'visible')};
  pointer-events: ${({ hidden_ }) => (hidden_ ? 'none' : 'auto')};
`

export const Caption = styled.div`
  font-size: 10px;
  line-height: 1.2;
  font-weight: 400;
  color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  opacity: 0.7;
  letter-spacing: 0.01em;
`

const toDisplayPercent = (value?: number | null, alreadyPercent = true): number | undefined => {
  if (value == null || Number.isNaN(value)) return undefined
  const pct = alreadyPercent ? value : value * 100
  return Math.max(0, Math.min(100, pct))
}

export default function FeeSelector({
  disabled = false,
  feeAmount,
  handleFeePoolSelect,
  currencyA,
  currencyB,
}: {
  disabled?: boolean
  feeAmount?: FeeAmount
  handleFeePoolSelect: (feeAmount: FeeAmount) => void
  currencyA?: Currency | undefined
  currencyB?: Currency | undefined
}) {
  const { chainId } = useConnection()
  const { isLoading, isError, largestUsageFeeTier, distributions, tvlAmounts } = useFeeTierDistribution(
    currencyA,
    currencyB
  )

  const poolAddresses = useMemo(() => {
    if (!currencyA || !currencyB || !chainId) return []

    const tokenA = currencyA.wrapped
    const tokenB = currencyB.wrapped
    const factoryAddress = V3_FACTORY_ADDRESSES[chainId]

    if (!factoryAddress) return []

    return [FeeAmount.LOWEST, FeeAmount.LOW, FeeAmount.MEDIUM, FeeAmount.HIGH, FeeAmount.EXTRA].map((fee) =>
      computePoolAddress({
        factoryAddress,
        tokenA: tokenB,
        tokenB: tokenA,
        fee,
      }).toLowerCase()
    )
  }, [currencyA, currencyB, chainId])

  const pools = useV3Pools(poolAddresses, chainId)

  const poolsByFeeTier: Record<FeeAmount, PoolState> = useMemo(
    () =>
      pools.reduce((acc, [curPoolState, curPool]) => ({ ...acc, [curPool?.fee as FeeAmount]: curPoolState }), {
        [FeeAmount.LOWEST]: PoolState.NOT_EXISTS,
        [FeeAmount.LOW]: PoolState.NOT_EXISTS,
        [FeeAmount.MEDIUM]: PoolState.NOT_EXISTS,
        [FeeAmount.HIGH]: PoolState.NOT_EXISTS,
        [FeeAmount.EXTRA]: PoolState.NOT_EXISTS,
      } as Record<FeeAmount, PoolState>),
    [pools]
  )

  const [showOptions, setShowOptions] = useState(true)

  const supportedTiers = useMemo(() => {
    if (!chainId) return [] as FeeAmount[]
    return [FeeAmount.LOWEST, FeeAmount.LOW, FeeAmount.MEDIUM, FeeAmount.HIGH, FeeAmount.EXTRA].filter((t) =>
      FEE_AMOUNT_DETAIL[t].supportedChains.includes(chainId)
    )
  }, [chainId])

  const pairKey = useMemo(() => {
    const a = currencyA?.wrapped?.address?.toLowerCase() ?? 'na'
    const b = currencyB?.wrapped?.address?.toLowerCase() ?? 'nb'
    return `${a}-${b}-${chainId ?? 'nc'}`
  }, [currencyA, currencyB, chainId])
  const lastAuto = useRef<string | null>(null)

  const recommendedByDistribution = useMemo(() => {
    if (!distributions || supportedTiers.length === 0) return undefined
    return supportedTiers.reduce((best: FeeAmount | undefined, cur) => {
      const b = best == null ? -Infinity : distributions[best] ?? -Infinity
      const c = distributions[cur] ?? -Infinity
      return c > b ? cur : best
    }, undefined)
  }, [distributions, supportedTiers])

  useEffect(() => {
    if (disabled) return
    if (feeAmount != null) return
    if (isLoading || isError) return
    if (lastAuto.current === pairKey) return

    if (recommendedByDistribution != null) {
      handleFeePoolSelect(recommendedByDistribution)
      lastAuto.current = pairKey
      return
    }
    if (largestUsageFeeTier != null && supportedTiers.includes(largestUsageFeeTier)) {
      handleFeePoolSelect(largestUsageFeeTier)
      lastAuto.current = pairKey
      return
    }
    const firstExisting = supportedTiers.find((t) => poolsByFeeTier[t] && poolsByFeeTier[t] !== PoolState.NOT_EXISTS)
    if (firstExisting != null) {
      handleFeePoolSelect(firstExisting)
      lastAuto.current = pairKey
      return
    }
    if (supportedTiers.includes(FeeAmount.MEDIUM)) {
      handleFeePoolSelect(FeeAmount.MEDIUM)
      lastAuto.current = pairKey
    }
  }, [
    disabled,
    feeAmount,
    isLoading,
    isError,
    pairKey,
    recommendedByDistribution,
    largestUsageFeeTier,
    supportedTiers,
    poolsByFeeTier,
    handleFeePoolSelect,
  ])

  const prev = usePrevious(feeAmount)
  const [pulsing, setPulsing] = useState(false)

  useEffect(() => {
    if (feeAmount && prev !== feeAmount) setPulsing(true)
  }, [prev, feeAmount])

  const selectFee = useCallback((fee: FeeAmount) => handleFeePoolSelect(fee), [handleFeePoolSelect])

  const selectedPercent =
    feeAmount != null && distributions ? toDisplayPercent(distributions[feeAmount], true) : undefined

  const selectedTVL = useMemo(() => {
    if (!feeAmount || !tvlAmounts) return undefined
    const v = tvlAmounts[feeAmount]
    return typeof v === 'number' ? v : undefined
  }, [feeAmount, tvlAmounts])

  const totalTVL = useMemo(() => {
    if (!tvlAmounts) return undefined
    let sum = 0
    for (const v of Object.values(tvlAmounts)) {
      if (typeof v === 'number' && Number.isFinite(v)) sum += v
    }
    return sum
  }, [tvlAmounts])

  const badge = (content?: React.ReactNode, width = 64) =>
    isLoading ? <TopBadge style={{ width }} /> : <TopBadge>{content ?? '–'}</TopBadge>

  return (
    <AutoColumn gap="16px">
      <DynamicSection gap="md" disabled={disabled}>
        <FocusedOutlineCard pulsing={pulsing} onAnimationEnd={() => setPulsing(false)}>
          <RowBetween>
            <AutoColumn id="add-liquidity-selected-fee">
              {!feeAmount ? (
                <>
                  <ThemedText.DeprecatedLabel>Fee tier</ThemedText.DeprecatedLabel>
                  <ThemedText.DeprecatedMain fontWeight={400} fontSize="12px" textAlign="left">
                    The % you will earn in fees.
                  </ThemedText.DeprecatedMain>
                </>
              ) : (
                <>
                  <ThemedText.DeprecatedLabel className="selected-fee-label">
                    <T.BodyM>{feeAmount ? `${FEE_AMOUNT_DETAIL[feeAmount].label}% fee tier` : 'Fee tier'}</T.BodyM>
                  </ThemedText.DeprecatedLabel>

                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {badge(selectedPercent != null ? `${Math.round(selectedPercent)}% share` : undefined)}
                  </div>

                  <div>
                    <Caption style={{ fontSize: '13px', opacity: 0.7, color: 'inherit', marginTop: '6px' }}>
                      {isLoading ? '...' : selectedTVL != null ? `${formatDollarAmount(selectedTVL)} TVL` : '–'}
                    </Caption>
                  </div>
                </>
              )}
            </AutoColumn>

            <ButtonGray
              width="auto"
              padding="4px"
              $borderRadius="6px"
              aria-controls="fee-options"
              aria-expanded={showOptions}
              style={{ fontSize: '12px' }}
              onClick={() => setShowOptions((s) => !s)}
            >
              {showOptions ? 'Hide' : 'Edit'}
            </ButtonGray>
          </RowBetween>
        </FocusedOutlineCard>

        <OptionsRegion hidden_={!showOptions} aria-hidden={!showOptions} id="fee-options">
          <FeeGrid>
            {supportedTiers.map((tier) => (
              <FeeOption
                key={tier}
                feeAmount={tier}
                active={feeAmount === tier}
                distributions={distributions}
                tvlAmounts={tvlAmounts}
                poolState={poolsByFeeTier[tier]}
                onClick={() => selectFee(tier)}
                isLoading={!!isLoading}
              />
            ))}
          </FeeGrid>
        </OptionsRegion>
      </DynamicSection>
    </AutoColumn>
  )
}
